import type { Gender, SortKey, Student } from '../types/student';

export interface StudentStats {
  total: number;
  classes: number;
  male: number;
  female: number;
  other: number;
  unspecifiedGender: number;
  recent: number;
}

const SEVEN_DAYS = 7 * 24 * 60 * 60 * 1000;

export function computeStats(students: Student[]): StudentStats {
  const classes = new Set<string>();
  let male = 0;
  let female = 0;
  let other = 0;
  let unspecifiedGender = 0;
  let recent = 0;
  const cutoff = Date.now() - SEVEN_DAYS;

  for (const student of students) {
    const className = student.className.trim();
    if (className) classes.add(className.toLowerCase());

    if (student.gender === 'Male') male += 1;
    else if (student.gender === 'Female') female += 1;
    else if (student.gender === 'Other') other += 1;
    else unspecifiedGender += 1;

    if (student.createdAtMs >= cutoff) recent += 1;
  }

  return { total: students.length, classes: classes.size, male, female, other, unspecifiedGender, recent };
}

export interface ClassBucket {
  className: string;
  count: number;
}

export function classDistribution(students: Student[]): ClassBucket[] {
  const buckets = new Map<string, number>();
  for (const student of students) {
    const label = student.className.trim() || 'Unassigned';
    buckets.set(label, (buckets.get(label) ?? 0) + 1);
  }
  return Array.from(buckets.entries())
    .map(([className, count]) => ({ className, count }))
    .sort((a, b) => b.count - a.count || a.className.localeCompare(b.className));
}

export function uniqueClasses(students: Student[]): string[] {
  const seen = new Map<string, string>();
  for (const student of students) {
    const label = student.className.trim();
    if (label && !seen.has(label.toLowerCase())) seen.set(label.toLowerCase(), label);
  }
  return Array.from(seen.values()).sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
}

export function filterStudents(students: Student[], search: string, gender: Gender, className: string): Student[] {
  const term = search.trim().toLowerCase();
  return students.filter((student) => {
    if (gender && student.gender !== gender) return false;
    if (className && student.className.trim().toLowerCase() !== className.toLowerCase()) return false;
    if (!term) return true;
    return [
      student.fullNames,
      student.className,
      student.gender,
      student.fatherName,
      student.fatherPhone,
      student.motherName,
      student.motherPhone,
      student.note
    ]
      .join(' ')
      .toLowerCase()
      .includes(term);
  });
}

export function sortStudents(students: Student[], key: SortKey): Student[] {
  const copy = [...students];
  switch (key) {
    case 'oldest':
      return copy.sort((a, b) => a.createdAtMs - b.createdAtMs);
    case 'name-asc':
      return copy.sort((a, b) => a.fullNames.localeCompare(b.fullNames, undefined, { sensitivity: 'base' }));
    case 'name-desc':
      return copy.sort((a, b) => b.fullNames.localeCompare(a.fullNames, undefined, { sensitivity: 'base' }));
    case 'class-asc':
      return copy.sort(
        (a, b) =>
          a.className.localeCompare(b.className, undefined, { numeric: true, sensitivity: 'base' }) ||
          a.fullNames.localeCompare(b.fullNames, undefined, { sensitivity: 'base' })
      );
    case 'newest':
    default:
      return copy.sort((a, b) => b.createdAtMs - a.createdAtMs);
  }
}
